import { useEffect } from "react";

/**
 * Renders all other elements inert except for the one specified by the ref.
 * For keeping a specific element interactive while making others non-interactive.
 * @param ref - A React ref object pointing to an HTML element to keep as-is.
 * @param active - A boolean indicating whether to apply inert behavior.
 */
export default function useInert(
    ref: React.RefObject<HTMLElement>,
    active: boolean,
) {
    useEffect(() => {
        if (!active) return;

        const element = ref.current;
        if (!element) return;

        // Get all parent elements of the ref element
        const parents = new Set<Element>();
        let current = element.parentElement;
        while (current) {
            parents.add(current);
            current = current.parentElement;
        }

        // Get all children elements of the ref element
        const children = new Set<Element>();
        const addChildren = (parent: Element) => {
            for (const child of parent.children) {
                children.add(child);
                addChildren(child);
            }
        };
        addChildren(element);

        // Set inert on all elements except the ref element, its parents, its children, and portal elements
        const allElements = document.querySelectorAll(
            "body *:not([data-inert]):not([data-portal='true'])",
        );
        for (const el of allElements) {
            if (el !== element && !parents.has(el) && !children.has(el)) {
                (el as HTMLElement).inert = true;
            }
        }

        // Remove inert from the specified element
        element.inert = false;

        return () => {
            // Cleanup: remove inert from all elements
            for (const el of allElements) {
                (el as HTMLElement).inert = false;
            }
        };
    }, [ref, active]);
}
